/**
 * EPIC-DM01: Demo Mode Metric Badge
 *
 * Compact qualitative badge showing a label + value coloured by severity.
 *   low    → green
 *   medium → gold
 *   high   → red
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Variants                                                          */
/* ------------------------------------------------------------------ */

const demoMetricBadgeVariants = cva(
  'flex flex-col items-start rounded-xl px-4 py-3 min-w-[100px]',
  {
    variants: {
      severity: {
        low: 'bg-demo-green-light',
        medium: 'bg-demo-gold-light',
        high: 'bg-demo-error-light',
      },
    },
    defaultVariants: {
      severity: 'low',
    },
  },
);

/** Maps severity to the value text colour */
const severityValueColor: Record<string, string> = {
  low: 'text-demo-green',
  medium: 'text-demo-gold-darkest',
  high: 'text-demo-error',
};

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface DemoMetricBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof demoMetricBadgeVariants> {
  /** Small top label describing the metric */
  label: string;
  /** Large bold value displayed below the label */
  value: string | number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const DemoMetricBadge = React.forwardRef<HTMLDivElement, DemoMetricBadgeProps>(
  ({ className, severity = 'low', label, value, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(demoMetricBadgeVariants({ severity }), className)}
        {...props}
      >
        <span className="text-[14px] text-demo-text-muted font-medium leading-tight">
          {label}
        </span>
        <span
          className={cn(
            'text-demo-xl font-bold leading-tight mt-0.5',
            severityValueColor[severity ?? 'low'],
          )}
        >
          {value}
        </span>
      </div>
    );
  },
);

DemoMetricBadge.displayName = 'DemoMetricBadge';

export { DemoMetricBadge, demoMetricBadgeVariants };
